import { computed, ref, shallowRef, toValue, type MaybeRefOrGetter } from 'vue'
import { useApiClient } from '~/services/api'
import { AppProblemModel } from '~/services/api/AppProblemModel'
import { resolveProblemMessageWithReference } from '~/services/api/resolveProblemMessageWithReference'
import { useLogger } from '~/composables/useLogger'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import { downloadBlob } from '~/utils/downloadBlob'
import { printPdfBlob } from '~/utils/printPdfBlob'
import { PrintWindowBlockedError } from '~/utils/PrintWindowBlockedError'

/**
 * Download and print of a legal agreement's PDF. Each failure resolves to a presentable
 * message in `actionProblem`.
 */
export const useLegalAgreementDocument = (legalAgreementId: MaybeRefOrGetter<string>) => {
  const legalAgreement = useApiClient().legalAgreement()
  const t = usePresentationTranslator()
  const logger = useLogger('useLegalAgreementDocument')

  const isDownloading = ref(false)
  const isPrinting = ref(false)
  const actionProblem = shallowRef<() => string>(() => '')

  const loadDocument = (): Promise<Blob> =>
    legalAgreement.downloadLegalAgreement(toValue(legalAgreementId))

  const resolveProblem = (error: unknown, fallback: string): string =>
    error instanceof PrintWindowBlockedError
      ? t('common.legalAgreement.errors.printBlocked')
      : resolveProblemMessageWithReference(AppProblemModel.normalize(error), t, fallback)

  const run = async (
    isBusy: typeof isDownloading,
    action: 'download' | 'print',
    failureMessage: () => string,
    work: () => Promise<void>
  ): Promise<void> => {
    isBusy.value = true
    actionProblem.value = () => ''
    try {
      await work()
    } catch (error: unknown) {
      logger.error('runDocumentAction', { action, error })
      actionProblem.value = () => resolveProblem(error, failureMessage())
    } finally {
      isBusy.value = false
    }
  }

  const download = (): Promise<void> =>
    run(
      isDownloading,
      'download',
      () => t('common.legalAgreement.errors.download'),
      async () => {
        downloadBlob(await loadDocument(), `mealz-legal-agreement-${toValue(legalAgreementId)}.pdf`)
      }
    )

  const print = (): Promise<void> =>
    run(
      isPrinting,
      'print',
      () => t('common.legalAgreement.errors.print'),
      async () => {
        await printPdfBlob(loadDocument)
      }
    )

  return {
    actionProblem: computed(() => actionProblem.value()),
    download,
    isDownloading,
    isPrinting,
    print
  }
}
